import { Calibration, CustomClaimValue, Device, DeviceCommand, DeviceCommands, Devices, HardwareStatuses, Targets } from "@app/shared";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Pages } from "..";
import { hasMediaRequests } from "../../../helpers";
import { toastSuccess, toastWarning } from "../../../helpers/toastHelpers";
import { useDialog, useFetch, useTargetPhoto } from "../../../hooks";
import { useEditModelDialog } from "../../../hooks/useEditModelDialog";
import { useHasClaim } from "../../../hooks/useHasClaim";
import { TabbedDashLayout } from "../../layouts/TabbedDashLayout";
import { ReportsView } from "../../reports/ReportsView";
import { DeviceBoundaries, DeviceFusion, DeviceMediaRequestsView, DeviceStatusView, DeviceVideoStream, EditDeviceCommand, EditDeviceDetails, IconLink, Icons, MissingResourceNotice } from "../../shared";
import { AuthorizeView } from "../../shared/AuthorizeView";
import { DeviceNotesView } from "../../shared/DeviceNotesView";
import { DeviceUpgradeView } from "../../shared/DeviceUpgradeView";
import { DropDownMenu } from "../../shared/DropDownMenu";
import { DeviceCommandTypes } from "../../shared/EditDeviceCommand/DeviceCommandEditor";
import { RequiresGoogleMaps } from "../../shared/maps/RequiresGoogleMaps";
import { NavSelectorDropdown } from "../../shared/NavSelectorDropdown";
import { DeviceErrorsTable } from "../settings/DeviceErrorsTable";
import { DeviceHealthTab } from "./DeviceHealthTab";

const DeviceCalibrationTab: FC<{ device: Device; onChange?: () => void }> = ({ device, onChange }) => {
    const [lastTarget] = useFetch(() => Targets.getLastTargetWithPhotoForDevice(device.id));
    const [recentStatuses] = useFetch(() => HardwareStatuses.getRecentForDevice(device.id, 20), [device.id]);
    const isSuperAdmin = useHasClaim(CustomClaimValue.SuperAdmin);
    const { photoUrl, hasPhoto } = useTargetPhoto(lastTarget?.externalId, device.id, device.organizationId);
    const backgroundImageUrl = hasPhoto ? photoUrl : undefined;

    // Average the last several accelerometer pitch readings (reported in
    // degrees by HardwareStatusController) to smooth out per-frame jitter,
    // then convert to radians for the calibration fitter.
    const accelerometerPitchRadians = useMemo(() => {
        if (!recentStatuses?.length) return undefined;
        const samples = recentStatuses
            .map(s => s.orientation?.pitch)
            .filter((p): p is number => typeof p === "number" && Number.isFinite(p));
        if (!samples.length) return undefined;
        const avgValue = samples.reduce((a, b) => a + b, 0) / samples.length;
        return avgValue / 1000; //Convert mrad to rad
    }, [recentStatuses]);

    // Persist the calibration result to the device. All four calibration
    // outputs (pitch, roll, yaw, height) map to their corresponding Device
    // fields. Angles are radians; height is meters.
    const handleApplyToDevice = async ({ pitchRad, rollRad, yawRad, heightM }: { pitchRad: number; rollRad: number; yawRad: number; heightM: number }) => {
        const updated = { ...device, fusionOffsetPitch: pitchRad, fusionOffsetRoll: rollRad, fusionOffsetYaw: yawRad, height: heightM } as Device;
        await Devices.save(updated);
        toastSuccess("Settings applied", "New calibration sent to the device.");
        onChange?.();
    };

    return (
        <Calibration
            deviceId={device.id!}
            backgroundImageUrl={backgroundImageUrl}
            deviceHeight={device.height}
            fusionOffsetPitchRadians={device.fusionOffsetPitch}
            fusionOffsetRollRadians={device.fusionOffsetRoll}
            fusionOffsetYawRadians={device.fusionOffsetYaw}
            accelerometerPitchRadians={accelerometerPitchRadians}
            isSuperAdmin={isSuperAdmin}
            onApplyToDevice={handleApplyToDevice}
        />
    );
};

export const DeviceDetails: FC = (props) => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const fromAlert = searchParams.get("ref") === "alert";
    const [device, errors, fetchHelpers] = useFetch(() => Devices.getById(id), [id]);

    const deviceDialog = useEditModelDialog(Device);
    const sendCommandDialog = useDialog<Device>();
    const [commandToSend, setCommandToSend] = useState<DeviceCommandTypes>();
    const commandToSendRef = useRef(commandToSend);

    useEffect(() => {
        commandToSendRef.current = commandToSend;
    }, [commandToSend]);

    useEffect(() => {
        if (!device && !fetchHelpers.isLoading && errors) {
            console.error("Error loading device", errors);
        }
    }, [errors, device, fetchHelpers.isLoading]);

    // The device exists but isn't assigned to a location. Warn the user (e.g. they
    // followed a location link from an alert email that couldn't open). Toast rather
    // than an inline banner so it's conspicuous (#772).
    useEffect(() => {
        if (device && !device.facilityId) {
            toastWarning(
                "Device not assigned to a location",
                `This device isn't currently assigned to a location${fromAlert ? ", which is why the location link in your alert email didn't open" : ""}. Assign it to a location to group it with that location's events.`
            );
        }
    }, [device?.id, device?.facilityId, fromAlert]);

    const handleEditOnClick = (device: Device) => {
        deviceDialog.show(
            async (device) => {
                await Devices.save(device as Device);
                fetchHelpers.refreshData();
            },
            "Edit Device",
            device
        );
    };

    const handleRequestSshClick = async (device: Device) => {
        await Devices.requestSshAccess(device.id);
        toastSuccess("Submitted", "An ssh request has been submitted.");
    };

    const handleSendCommandClick = (device: Device) => {
        setCommandToSend(undefined);
        sendCommandDialog.show("Send Device Command", "Send", device, async () => {
            await DeviceCommands.sendCommand(commandToSendRef.current as DeviceCommand);
            toastSuccess("Command Created", "A new command is pending for this device.");
            fetchHelpers.refreshData();
        });
    };

    const NotesTitle = useMemo(() => {
        const notes = device?.notes?.filter((n) => !n.isDeleted);
        if (!!!notes?.length) return "Notes";
        return (
            <>
                <span>Notes</span>
                <div className="w-5 h-5 ml-1 rounded-full bg-brand-primary flex items-center justify-center text-bright-text text-xs font-bold mt-px">{notes.length}</div>
            </>
        );
    }, [device?.notes]);

    const hasMRVersion = hasMediaRequests(device?.firmwareVersion);
    const canAccessUpgrade = useHasClaim(CustomClaimValue.EditDetails, device?.organizationId, device?.facilityId);
    // Matches DeviceController.Save / DeviceCommandController.SendCommand.
    const canManageDevice = useHasClaim(CustomClaimValue.ManageSettings, device?.organizationId, device?.facilityId);

    // The device couldn't be loaded: removed, or the user lacks access. Explain
    // instead of silently redirecting to the (super-admin) devices list (#772).
    // Placed after all hooks so they run unconditionally (rules of hooks).
    if (!device && !fetchHelpers.isLoading) {
        return (
            <MissingResourceNotice
                title="Device not available"
                icon={<Icons.AlertTriangle size={64} />}
                message={fromAlert
                    ? "We couldn't open the device from this alert. It may have been removed, or your account may not have access to it. If you believe this is a mistake, contact your administrator."
                    : "This device couldn't be found. It may have been removed, or you may not have access to it."}
                actions={[{ label: "Go to my locations", to: Pages.ViewFacilityGroups.getUrl(), primary: true }]}
            />
        );
    }

    return !!!device ? null : (
        <>
            <TabbedDashLayout
                leftChildren={
                    <NavSelectorDropdown
                        mode="locations-devices"
                        triggerLabel={device.name ?? "Loading..."}
                        initialOrgId={device.organizationId}
                        initialFacilityId={device.facilityId}
                        initialDeviceId={id}
                    />
                }
                tabs={[
                    { title: "Status", child: <DeviceStatusView device={device} onChange={fetchHelpers.refreshData} /> },
                    { title: "Health", requiredClaim: CustomClaimValue.SuperAdmin, child: <DeviceHealthTab device={device} /> },
                    { title: "Camera", child: <DeviceVideoStream device={device} /> },
                    ...(hasMRVersion
                        ? [
                            {
                                title: "Media Requests",
                                child: <DeviceMediaRequestsView device={device} onChange={fetchHelpers.refreshData} />,
                            },
                        ]
                        : []),
                    {
                        title: "Boundary",
                        child: (
                            <RequiresGoogleMaps>
                                <DeviceBoundaries device={device} onChange={fetchHelpers.refreshData} />
                            </RequiresGoogleMaps>
                        ),
                    },
                    {
                        title: "Fusion",
                        child: <DeviceFusion device={device} onChange={fetchHelpers.refreshData} />
                    },
                    { title: "Reports", child: <ReportsView device={device} onChange={fetchHelpers.refreshData} /> },
                    { title: NotesTitle, requiredClaim: CustomClaimValue.SuperAdmin, child: <DeviceNotesView device={device} onChange={fetchHelpers.refreshData} /> },
                    { title: "Errors", requiredClaim: CustomClaimValue.SuperAdmin, child: <DeviceErrorsTable device={device} /> },
                    { title: "Calibration", child: <DeviceCalibrationTab device={device} onChange={fetchHelpers.refreshData} /> },
                    ...(canAccessUpgrade ? [{ title: "Upgrade", child: <DeviceUpgradeView device={device} /> }] : [])
                ]}
                centerChildren={device?.name ?? "Loading..."}
                rightChildren={canManageDevice && (
                    <DropDownMenu toggleChild={<Icons.Settings className="w-7 h-7" />}>
                        <IconLink Icon={Icons.Edit} onClick={() => handleEditOnClick(device!)}>
                            Edit
                        </IconLink>
                        <IconLink Icon={Icons.Send} onClick={() => handleSendCommandClick(device!)}>
                            Send Command
                        </IconLink>
                        <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
                            <IconLink Icon={Icons.Send} onClick={() => handleRequestSshClick(device!)}>
                                Open SSH Request
                            </IconLink>
                        </AuthorizeView>
                    </DropDownMenu>
                )}
            />
            {deviceDialog.renderModal((model, helpers, onSubmit, onCancel) => (
                <EditDeviceDetails onSubmit={onSubmit} onCancel={onCancel} device={model} helpers={helpers} />
            ), "w-full max-w-5xl rounded-2xl")}
            {sendCommandDialog.renderDialog(
                (device) => (
                    <EditDeviceCommand command={commandToSend} firmwareVersion={device?.firmwareVersion} deviceId={device?.id ?? ""} onChange={(command) => setCommandToSend(command)} />
                ),
                { closeOnOutsideClick: false }
            )}
        </>
    );
};
